import { FC, Fragment, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import axios from "../../utils/axios";
import { useAppSelector } from "../../store/hooks";

// Inventory data purge — dry-run preview of every row a purge would delete, then
// an owner-only, type-to-confirm delete. Mirrors scripts/purgeInventoryData.js.

type Row = { id: string; text: string; date?: string; del: boolean };
type Target = { key: string; label: string; count: number; samples: string[]; rows: Row[] };
type Preview = {
  targets: Target[];
  items: { rows: Target[]; lineItemRefs: number };
  journalEntries: { refTypes: string[]; count: number };
  flagged: { key: string; label: string; count: number }[];
};

// Column headers for each category's preview rows.
const COLS: Record<string, string[]> = {
  movements: ["Item", "Location", "Reason", "Qty", "Balance after"],
  stockLevels: ["Item", "Location", "Qty", "Cost"],
  grns: ["GRN no.", "Status", "Items"],
  transfers: ["Transfer no.", "Status", "Items"],
  requisitions: ["Requisition no.", "Status", "Items"],
  batches: ["Batch", "Item", "Expiry"],
  skuBarcodes: ["Code", "Item"],
  skuHistory: ["Item", "Updated"],
  recipes: ["Menu item", "Components"],
  skus: ["Name", "Code", "Unit"],
};
const cells = (text: string) => text.split(/ · | @ /).map((c) => c.replace(/^(qty|cost|after|exp|Batch) /, ""));

const InventoryPurge: FC = () => {
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const accountId = selectedAccount?._id || "";

  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string>("");
  const [skip, setSkip] = useState<string[]>([]);
  const [sel, setSel] = useState<string[]>([]);
  const [withItems, setWithItems] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/v1/erp/inventory/purge/preview?sampleSize=200");
      setPreview(res.data?.data || null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sum = (rows: Target[]) => rows.reduce((n, t) => n + t.count, 0);
  const picked = (preview?.targets || []).filter((t) => !skip.includes(t.key));
  const total = sum(picked) + (withItems ? sum(preview?.items.rows || []) : 0);

  const onPurge = async () => {
    setPurging(true);
    try {
      const res = await axios.post("/api/v1/erp/inventory/purge", { confirm, keys: picked.map((t) => t.key), withJournals: true, withItems });
      const deleted = res.data?.data || {};
      toast.success(`Deleted ${Object.values(deleted).reduce((a: any, b: any) => a + b, 0)} row(s)`);
      setDialogOpen(false);
      setConfirm("");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  const onDeleteRows = async (key: string, ids: string[], text: string) => {
    if (!window.confirm(`Delete ${text}? Stock on hand and journal entries are reversed too. This cannot be undone.`)) return;
    try {
      await axios.post("/api/v1/erp/inventory/purge", { key, ids });
      toast.success(`Deleted ${ids.length} row(s)`);
      setSel([]);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Delete failed");
    }
  };

  const rows = (list: Target[], pick = false) =>
    list.map((t) => (
      <Fragment key={t.key}>
        <TableRow
          hover
          sx={{ cursor: t.count ? "pointer" : "default" }}
          onClick={() => t.count && (setSel([]), setExpanded(expanded === t.key ? "" : t.key))}
        >
          <TableCell sx={{ width: 40 }}>
            {t.count ? (expanded === t.key ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />) : null}
          </TableCell>
          <TableCell>
            {pick && (
              <Checkbox
                size="small"
                sx={{ p: 0, mr: 1 }}
                checked={!skip.includes(t.key)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => setSkip(skip.includes(t.key) ? skip.filter((k) => k !== t.key) : [...skip, t.key])}
              />
            )}
            {t.label}
          </TableCell>
          <TableCell align="right">
            <Chip size="small" label={t.count} color={t.count ? "error" : "default"} variant="outlined" />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={3} sx={{ p: 0, border: 0 }}>
            <Collapse in={expanded === t.key} unmountOnExit>
              <Box sx={{ px: 4, pb: 1, bgcolor: "#fafafa", fontSize: "12px", fontFamily: "monospace", maxHeight: 320, overflowY: "auto" }}>
                {pick && t.rows.some((r) => r.del) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, position: "sticky", top: 0, bgcolor: "#fafafa", zIndex: 1 }}>
                    <Checkbox
                      size="small"
                      sx={{ p: 0 }}
                      checked={sel.length > 0 && sel.length === t.rows.filter((r) => r.del).length}
                      onChange={(e) => setSel(e.target.checked ? t.rows.filter((r) => r.del).map((r) => r.id) : [])}
                    />
                    <span>Select all</span>
                    <Button size="small" color="error" variant="contained" disabled={!sel.length} onClick={() => onDeleteRows(t.key, sel, `${sel.length} selected row(s)`)}>
                      Delete {sel.length} selected
                    </Button>
                  </Box>
                )}
                <Table size="small" sx={{ "& td, & th": { fontSize: "12px", py: 0.25, px: 1, borderRight: "1px solid #e0e0e0" } }}>
                  <TableHead>
                    <TableRow>
                      {pick && <TableCell sx={{ width: 28 }} />}
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Date</TableCell>
                      {(COLS[t.key] || []).map((c) => (
                        <TableCell key={c} sx={{ fontWeight: 600 }}>{c}</TableCell>
                      ))}
                      {pick && <TableCell sx={{ width: 28 }} />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {t.rows.map((r) => (
                      <TableRow key={r.id} hover>
                        {pick && (
                          <TableCell>
                            {r.del && (
                              <Checkbox
                                size="small"
                                sx={{ p: 0 }}
                                checked={sel.includes(r.id)}
                                onChange={() => setSel(sel.includes(r.id) ? sel.filter((i) => i !== r.id) : [...sel, r.id])}
                              />
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.date ? new Date(r.date).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </TableCell>
                        {cells(r.text).map((c, i) => (
                          <TableCell key={i}>{c}</TableCell>
                        ))}
                        {pick && (
                          <TableCell>
                            {r.del && (
                              <IconButton size="small" color="error" sx={{ p: 0 }} onClick={() => onDeleteRows(t.key, [r.id], `"${r.text}"`)}>
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {t.count > t.rows.length && <div>… {t.count - t.rows.length} more</div>}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </Fragment>
    ));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h6">Purge Inventory Data</Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            Deletes GRNs, stock on hand, movements, batches, requisitions and transfers for{" "}
            <b>{selectedAccount?.displayName || selectedAccount?.name || accountId}</b>. Items, locations
            and units are not touched.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        This cannot be undone. Review the preview below before deleting.
      </Alert>

      {loading && !preview ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Rows to delete
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{rows(preview?.targets || [], true)}</TableBody>
          </Table>
        </Paper>
      )}

      {preview && (
        <Paper variant="outlined" sx={{ mt: 2 }}>
          <Box sx={{ px: 2, pt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={withItems} onChange={(e) => setWithItems(e.target.checked)} />}
              label="Also delete item masters (SKUs, barcodes, history, recipes)"
            />
          </Box>
          <Table size="small">
            <TableBody sx={{ opacity: withItems ? 1 : 0.5 }}>{rows(preview.items.rows)}</TableBody>
          </Table>
          {withItems && preview.items.lineItemRefs > 0 && (
            <Alert severity="error" sx={{ m: 2 }}>
              {preview.items.lineItemRefs} invoice/estimate line item(s) point at these items and will be
              left referencing deleted SKUs.
            </Alert>
          )}
        </Paper>
      )}

      {preview && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Journal entries of the deleted stock rows are deleted with them.
          </Alert>

          {preview.flagged.some((f) => f.count > 0) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Not deleted, but they reference stock and batches:{" "}
              {preview.flagged.filter((f) => f.count).map((f) => `${f.label} (${f.count})`).join(", ")}.
            </Alert>
          )}

          <Button
            variant="contained"
            color="error"
            disabled={!total}
            onClick={() => setDialogOpen(true)}
          >
            Delete {total} row(s)
          </Button>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Confirm purge</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {total} row(s) will be permanently deleted, along with their journal entries.
            Type the account id <b>{accountId}</b> to confirm.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={accountId}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={confirm !== accountId || purging}
            onClick={onPurge}
          >
            {purging ? "Deleting…" : "Delete permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryPurge;
